/**
 * PWA Detection Utilities
 * Helpers for detecting iOS Safari and PWA install state
 */

/**
 * Check if the device is running iOS (iPhone, iPad, iPod)
 */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

/**
 * Check if the browser is Safari (not Chrome, Firefox, etc. on iOS)
 */
export function isSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // Safari includes "Safari" but Chrome/Firefox on iOS include their own identifiers
  return /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

/**
 * Check if the browser is iOS Safari specifically
 */
export function isIOSSafari(): boolean {
  return isIOS() && isSafari();
}

/**
 * Check if the app is running in standalone mode (installed as PWA)
 */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;

  // Check display-mode media query (works on most browsers)
  const displayModeStandalone = window.matchMedia('(display-mode: standalone)').matches;

  // Check iOS-specific standalone property
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const iosStandalone = (window.navigator as any).standalone === true;

  return displayModeStandalone || iosStandalone;
}

/**
 * Check if the app is running as a PWA (installed to home screen)
 */
export function isPWA(): boolean {
  return isStandalone();
}

/**
 * Check if push notifications are supported in this browser/context
 */
export function isPushSupported(): boolean {
  if (typeof window === 'undefined') return false;
  return 'PushManager' in window && 'serviceWorker' in navigator;
}

/**
 * Check if the current notification permission status
 */
export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

/**
 * Check if we should show the iOS Safari install banner
 * Shows if:
 * - User is on iOS Safari
 * - App is not already installed (not in standalone mode)
 * - User hasn't dismissed the banner (stored in localStorage)
 */
export function shouldShowInstallBanner(): boolean {
  if (typeof window === 'undefined') return false;

  // Only show on iOS Safari
  if (!isIOSSafari()) return false;

  // Don't show if already installed
  if (isStandalone()) return false;

  // Check if user has dismissed the banner
  const dismissed = localStorage.getItem('pwa-install-dismissed');
  if (dismissed) {
    // Check if dismissal is recent (within 7 days)
    const dismissedDate = new Date(dismissed);
    const now = new Date();
    const daysSinceDismissal = (now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceDismissal < 7) {
      return false;
    }
    // Clear old dismissal
    localStorage.removeItem('pwa-install-dismissed');
  }

  return true;
}

/**
 * Mark the install banner as dismissed
 */
export function dismissInstallBanner(): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  }
}

/**
 * Get iOS version if on iOS, returns null otherwise
 */
export function getIOSVersion(): number | null {
  if (!isIOS()) return null;

  const match = navigator.userAgent.match(/OS (\d+)_/);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return null;
}

/**
 * Check if iOS version supports web push (16.4+)
 */
export function supportsIOSWebPush(): boolean {
  const version = getIOSVersion();
  if (version === null) return false;
  // Web Push support was added in iOS 16.4
  // We check for 16 as the major version, actual support requires 16.4+
  return version >= 16;
}
